import { markLaunchPending } from '../../components/brand/LaunchSequence';
import { useState } from 'react';
import { BiicLockup } from '../../components/brand/BiicLogo';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { login, verifyMfa } from '../../lib/auth/AuthContext';
import { useAuthStore } from '../../lib/auth/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

type Mode = 'login' | 'mfa' | 'forgot' | 'forgot_sent';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('session') === 'expired';
  const { setMfaRequired, mfaRequired, challengeId } = useAuthStore();

  const [mode, setMode]                 = useState<Mode>('login');
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [mfaCode, setMfaCode]           = useState('');
  const [useRecovery, setUseRecovery]   = useState(false);
  const [forgotEmail, setForgotEmail]   = useState('');

  // Keep mode in sync with mfaRequired from store
  const currentMode: Mode = mfaRequired ? 'mfa' : mode;

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.mfa_required) {
        setMfaRequired(true, result.challenge_id);
      } else if (result.access_token && result.user) {
        useAuthStore.getState().login(result.user, result.access_token, result.force_password_change);
        markLaunchPending();
        navigate('/app');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaVerify(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await verifyMfa(challengeId!, mfaCode, useRecovery);
      useAuthStore.getState().login(result.user, result.access_token, result.force_password_change ?? false);
      markLaunchPending();
      navigate('/app');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MFA verification failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: { message?: string } };
        throw new Error(data.error?.message ?? 'Request failed');
      }
      setMode('forgot_sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  let formContent: React.ReactNode;

  if (currentMode === 'mfa') {
    formContent = (
      <>
        <FormHead
          eyebrow="Verify"
          title="Two-factor authentication"
          sub={useRecovery ? 'Enter one of your recovery codes.' : 'Enter the code from your authenticator app.'}
        />
        <form onSubmit={handleMfaVerify} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>{useRecovery ? 'Recovery code' : 'Authentication code'}</label>
            <input
              type="text"
              inputMode={useRecovery ? 'text' : 'numeric'}
              pattern={useRecovery ? undefined : '[0-9]*'}
              maxLength={useRecovery ? 19 : 6}
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value)}
              placeholder={useRecovery ? 'XXXXXXXX-XXXXXXXX' : '000000'}
              autoFocus
              required
              style={inputStyle}
            />
          </div>
          {error && <div role="alert" style={errorNoteStyle}>{error}</div>}
          <button type="submit" disabled={loading} style={submitStyle}>
            {loading ? <Loader2 size={16} style={{ display: 'inline', marginRight: 8 }} /> : null}
            Verify
          </button>
          <button
            type="button"
            onClick={() => { setUseRecovery(!useRecovery); setMfaCode(''); setError(''); }}
            style={ghostBtnStyle}
          >
            {useRecovery ? 'Use authenticator app instead' : "Can't access your app? Use a recovery code"}
          </button>
        </form>
      </>
    );
  } else if (currentMode === 'forgot') {
    formContent = (
      <>
        <FormHead eyebrow="Reset" title="Reset password" sub="Enter your email and we'll send a reset link." />
        <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Email address</label>
            <input
              type="email"
              value={forgotEmail}
              onChange={e => setForgotEmail(e.target.value)}
              placeholder="you@example.com"
              autoFocus
              required
              style={inputStyle}
            />
          </div>
          {error && <div role="alert" style={errorNoteStyle}>{error}</div>}
          <button type="submit" disabled={loading} style={submitStyle}>
            {loading ? <Loader2 size={16} style={{ display: 'inline', marginRight: 8 }} /> : null}
            Send reset link
          </button>
          <button type="button" onClick={() => { setMode('login'); setError(''); }} style={ghostBtnStyle}>
            Back to sign in
          </button>
        </form>
      </>
    );
  } else if (currentMode === 'forgot_sent') {
    formContent = (
      <div>
        <FormHead eyebrow="Reset link sent" title="Check your email" />
        <div style={{ background: 'rgba(0,122,77,.08)', border: '1px solid rgba(0,122,77,.25)', borderRadius: 8, padding: '12px 14px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#00603C" strokeWidth={2.2} width={16} height={16} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <p style={{ fontSize: 13.5, color: '#1f2a24', lineHeight: 1.55, margin: 0 }}>
            If <strong style={{ fontWeight: 600 }}>{forgotEmail}</strong> is registered, you'll receive a reset link within a few minutes.
          </p>
        </div>
        <button type="button" onClick={() => { setMode('login'); setError(''); }} style={ghostBtnStyle}>
          Back to sign in
        </button>
      </div>
    );
  } else {
    formContent = (
      <>
        <FormHead eyebrow="Sign in" title="Welcome to BIIC" sub="Sign in to continue." />
        {sessionExpired && (
          <div style={{ ...errorNoteStyle, marginBottom: 16 }}>
            Your session has expired — please sign in again.
          </div>
        )}
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                style={{ ...inputStyle, paddingRight: 48 }}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onMouseDown={e => e.preventDefault()}
                onClick={() => setShowPassword(v => !v)}
                style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, background: 'none', border: 'none', cursor: 'pointer', color: '#4d5a52', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, padding: 0, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setForgotEmail(email); setMode('forgot'); setError(''); }}
            style={{ background: 'none', border: 'none', color: '#1e7a3a', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textAlign: 'right', padding: '0 0 4px', fontFamily: 'inherit', alignSelf: 'flex-end' }}
          >
            Forgot password?
          </button>
          {error && <div role="alert" style={errorNoteStyle}>{error}</div>}
          <button type="submit" disabled={loading} style={submitStyle}>
            {loading ? <Loader2 size={16} style={{ display: 'inline', marginRight: 8 }} /> : null}
            Sign in
          </button>
        </form>
      </>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f3f5f2', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'Inter Tight', system-ui, sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e0e6df', boxShadow: '0 1px 2px rgba(16,40,24,.04)', padding: 32, color: '#1f2a24' }}>
          {/* Brand block */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
            <BiicLockup size={48} caption="Truth in trade" />
          </div>
          {formContent}
        </div>
        <p style={{ textAlign: 'center', fontSize: 12, color: '#66736b', fontWeight: 400, margin: 0 }}>
          Brand Integrity Intelligence Centre
        </p>
      </div>
    </div>
  );
}

const TITLE = "'Jost', 'Inter Tight', sans-serif";

function FormHead({ eyebrow, title, sub }: { eyebrow: string; title: string; sub?: string }) {
  return (
    <div style={{ marginBottom: 24 }}>

      <div style={{ fontFamily: TITLE, fontWeight: 600, fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: '#66736b' }}>{eyebrow}</div>
      <h1 style={{ margin: '8px 0 0', fontFamily: TITLE, fontSize: 30, fontWeight: 500, letterSpacing: '-.005em', color: '#16211b', lineHeight: 1.05 }}>{title}</h1>
      {sub && <p style={{ color: '#4d5a52', fontSize: 14, margin: '10px 0 0', fontWeight: 400 }}>{sub}</p>}
    </div>
  );
}

const errorNoteStyle: React.CSSProperties = {
  background: 'rgba(222,56,49,.08)', border: '1px solid rgba(222,56,49,.22)', borderRadius: 8,
  padding: '10px 14px', color: '#B12E28', fontSize: 13, lineHeight: 1.5,
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: TITLE, fontWeight: 600, fontSize: 10.5, color: '#4d5a52',
  textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 14px', borderRadius: 8,
  border: '1px solid rgba(0,0,0,.12)',
  background: '#fff',
  color: '#1f2a24', fontSize: 14.5, fontWeight: 400,
  fontFamily: "'Inter Tight', system-ui, sans-serif",
  outline: 'none', transition: 'border-color .2s ease', boxSizing: 'border-box',
};

const submitStyle: React.CSSProperties = {
  marginTop: 8, width: '100%', padding: '13px 20px', borderRadius: 8,
  border: 'none', background: '#0d3318', color: '#fff',
  fontSize: 14.5, fontWeight: 600,
  fontFamily: "'Inter Tight', system-ui, sans-serif",
  cursor: 'pointer', letterSpacing: '.01em', transition: 'opacity .2s ease',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
};

const ghostBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#1e7a3a', fontWeight: 500,
  fontSize: 13, cursor: 'pointer', textAlign: 'center', padding: '4px 0',
  fontFamily: "'Inter Tight', system-ui, sans-serif",
};
